import api from './api';
import type {
  Asset,
  AssetSummary,
  AssetListResponse,
  AssetGroupedResponse,
  AssetTypesResponse,
  AssetFormData,
  AssetUsage,
  AssetUsageStats,
  RagModule,
  RagPipeline,
  ProjectRagConfig,
} from '@/types';

// ===========================================
// Asset API Service
// ===========================================

// Utility to transform snake_case to camelCase
function transformAsset(data: Record<string, unknown>): Asset {
  return {
    id: data.id as string,
    name: data.name as string,
    slug: data.slug as string,
    description: data.description as string | undefined,
    icon: data.icon as string | undefined,
    assetType: data.asset_type as Asset['assetType'],
    scope: data.scope as Asset['scope'],
    category: data.category as string | undefined,
    version: data.version as string,
    tags: (data.tags as string[]) || [],
    isBuiltin: data.is_builtin as boolean,
    mcpCompatible: data.mcp_compatible as boolean,
    mcpEndpoint: data.mcp_endpoint as string | undefined,
    usageCount: data.usage_count as number,
    status: data.status as Asset['status'],
    config: data.config as Record<string, unknown> | undefined,
    parametersSchema: data.parameters_schema as Record<string, unknown> | undefined,
    metadata: data.metadata as Record<string, unknown> | undefined,
    dependencyIds: data.dependency_ids as string[] | undefined,
    dependencies: (data.dependencies as Array<Record<string, unknown>>)?.map(transformAssetSummary),
    projectId: data.project_id as string | undefined,
    createdBy: data.created_by as { id: string; name: string } | undefined,
    createdAt: data.created_at as string,
    updatedAt: data.updated_at as string | undefined,
  };
}

function transformAssetSummary(data: Record<string, unknown>): AssetSummary {
  return {
    id: data.id as string,
    name: data.name as string,
    slug: data.slug as string,
    description: data.description as string | undefined,
    icon: data.icon as string | undefined,
    assetType: data.asset_type as AssetSummary['assetType'],
    category: data.category as string | undefined,
    tags: (data.tags as string[]) || [],
    isBuiltin: data.is_builtin as boolean,
    mcpCompatible: data.mcp_compatible as boolean,
    usageCount: data.usage_count as number,
    status: data.status as AssetSummary['status'],
  };
}

function transformRagModule(data: Record<string, unknown>): RagModule {
  return {
    id: data.id as string,
    name: data.name as string,
    slug: data.slug as string,
    description: data.description as string | undefined,
    icon: data.icon as string | undefined,
    moduleType: data.module_type as RagModule['moduleType'],
    config: (data.config as Record<string, unknown>) || {},
    tags: (data.tags as string[]) || [],
    isBuiltin: data.is_builtin as boolean,
    usageCount: data.usage_count as number,
  };
}

function transformRagPipeline(data: Record<string, unknown>): RagPipeline {
  const components = data.components as Record<string, Record<string, unknown>> | undefined;
  return {
    id: data.id as string,
    name: data.name as string,
    slug: data.slug as string,
    description: data.description as string | undefined,
    icon: data.icon as string | undefined,
    config: data.config as RagPipeline['config'],
    components: {
      chunker: components?.chunker ? transformRagModule(components.chunker) : undefined,
      embedder: components?.embedder ? transformRagModule(components.embedder) : undefined,
      retriever: components?.retriever ? transformRagModule(components.retriever) : undefined,
      reranker: components?.reranker ? transformRagModule(components.reranker) : undefined,
    },
    tags: (data.tags as string[]) || [],
    isBuiltin: data.is_builtin as boolean,
    usageCount: data.usage_count as number,
  };
}

// ===========================================
// Global Asset APIs
// ===========================================

export interface AssetListParams {
  projectId?: string;
  scope?: 'system' | 'organization' | 'project';
  type?: string;
  category?: string;
  tag?: string;
  builtin?: boolean;
  mcpCompatible?: boolean;
  q?: string;
  sort?: 'name' | 'usage' | 'created_at';
  order?: 'asc' | 'desc';
  page?: number;
  perPage?: number;
}

export async function getAssets(params: AssetListParams = {}): Promise<AssetListResponse> {
  const queryParams = new URLSearchParams();

  if (params.projectId) queryParams.append('project_id', params.projectId);
  if (params.scope) queryParams.append('scope', params.scope);
  if (params.type) queryParams.append('type', params.type);
  if (params.category) queryParams.append('category', params.category);
  if (params.tag) queryParams.append('tag', params.tag);
  if (params.builtin !== undefined) queryParams.append('builtin', String(params.builtin));
  if (params.mcpCompatible !== undefined) queryParams.append('mcp_compatible', String(params.mcpCompatible));
  if (params.q) queryParams.append('q', params.q);
  if (params.sort) queryParams.append('sort', params.sort);
  if (params.order) queryParams.append('order', params.order);
  if (params.page) queryParams.append('page', String(params.page));
  if (params.perPage) queryParams.append('per_page', String(params.perPage));

  const response = await api.get(`/assets?${queryParams.toString()}`);
  return {
    assets: response.data.assets.map(transformAssetSummary),
    pagination: response.data.pagination,
  };
}

export async function getAssetsGrouped(projectId?: string): Promise<AssetGroupedResponse> {
  const url = projectId ? `/assets/grouped?project_id=${projectId}` : '/assets/grouped';
  const response = await api.get(url);

  const assets: Record<string, AssetSummary[]> = {};
  for (const [type, list] of Object.entries(response.data.assets)) {
    assets[type] = (list as Array<Record<string, unknown>>).map(transformAssetSummary);
  }

  return {
    assets: assets as AssetGroupedResponse['assets'],
    counts: response.data.counts,
  };
}

export async function getAssetTypes(): Promise<AssetTypesResponse> {
  const response = await api.get('/assets/types');
  return response.data;
}

export async function getAsset(id: string): Promise<Asset> {
  const response = await api.get(`/assets/${id}`);
  return transformAsset(response.data.asset);
}

export async function getAssetBySlug(slug: string): Promise<Asset> {
  const response = await api.get(`/assets/by_slug/${slug}`);
  return transformAsset(response.data.asset);
}

export async function createAsset(data: AssetFormData, projectId?: string): Promise<Asset> {
  const payload = {
    asset: {
      name: data.name,
      slug: data.slug,
      description: data.description,
      icon: data.icon,
      asset_type: data.assetType,
      scope: data.scope,
      category: data.category,
      version: data.version || '1.0.0',
      tags: data.tags,
      mcp_compatible: data.mcpCompatible,
      mcp_endpoint: data.mcpEndpoint,
      config: data.config,
      parameters_schema: data.parametersSchema,
      metadata: data.metadata,
      dependency_ids: data.dependencyIds,
    },
    project_id: projectId,
  };

  const response = await api.post('/assets', payload);
  return transformAsset(response.data.asset);
}

export async function updateAsset(id: string, data: Partial<AssetFormData>): Promise<Asset> {
  const payload = {
    asset: {
      name: data.name,
      description: data.description,
      icon: data.icon,
      category: data.category,
      version: data.version,
      tags: data.tags,
      mcp_compatible: data.mcpCompatible,
      mcp_endpoint: data.mcpEndpoint,
      config: data.config,
      parameters_schema: data.parametersSchema,
      metadata: data.metadata,
      dependency_ids: data.dependencyIds,
    },
  };

  const response = await api.patch(`/assets/${id}`, payload);
  return transformAsset(response.data.asset);
}

export async function deleteAsset(id: string): Promise<void> {
  await api.delete(`/assets/${id}`);
}

export async function getAssetStats(id: string): Promise<AssetUsageStats> {
  const response = await api.get(`/assets/${id}/stats`);
  const stats = response.data.stats;

  return {
    totalCount: stats.total_count,
    successCount: stats.success_count,
    failureCount: stats.failure_count,
    successRate: stats.success_rate,
    avgDurationMs: stats.avg_duration_ms,
    totalTokens: stats.total_tokens,
    projectsUsing: stats.projects_using,
    recentUsages: stats.recent_usages?.map(transformAssetUsage) || [],
  };
}

// ===========================================
// RAG Module APIs
// ===========================================

export async function getRagModules(moduleType?: string): Promise<RagModule[]> {
  const url = moduleType ? `/assets/rag_modules?module_type=${moduleType}` : '/assets/rag_modules';
  const response = await api.get(url);
  return response.data.modules.map(transformRagModule);
}

export async function getRagPipelines(): Promise<RagPipeline[]> {
  const response = await api.get('/assets/rag_pipelines');
  return response.data.pipelines.map(transformRagPipeline);
}

// ===========================================
// Project RAG Configuration APIs
// ===========================================

export async function getProjectRagConfig(projectId: string): Promise<ProjectRagConfig> {
  const response = await api.get(`/projects/${projectId}/rag/config`);
  const data = response.data;

  return {
    config: {
      pipelineId: data.config.pipeline_id,
      chunker: data.config.chunker ? transformRagModule(data.config.chunker) : undefined,
      embedder: data.config.embedder ? transformRagModule(data.config.embedder) : undefined,
      retriever: data.config.retriever ? transformRagModule(data.config.retriever) : undefined,
      reranker: data.config.reranker ? transformRagModule(data.config.reranker) : undefined,
      customConfig: data.config.custom_config,
    },
    effectiveConfig: data.effective_config,
    settings: {
      pipelineId: data.settings.pipeline_id,
      pipelineSlug: data.settings.pipeline_slug,
      chunker: data.settings.chunker,
      embedder: data.settings.embedder,
      retriever: data.settings.retriever,
      reranker: data.settings.reranker,
      customConfig: data.settings.custom_config,
    },
  };
}

export async function applyRagPreset(projectId: string, pipelineSlug: string): Promise<{ message: string; settings: Record<string, unknown> }> {
  const response = await api.post(`/projects/${projectId}/rag/apply_preset`, {
    pipeline_slug: pipelineSlug,
  });
  return response.data;
}

export async function composeRagPipeline(
  projectId: string,
  config: {
    chunker?: string;
    embedder?: string;
    retriever?: string;
    reranker?: string;
  }
): Promise<{ message: string; settings: Record<string, unknown> }> {
  const response = await api.post(`/projects/${projectId}/rag/compose`, config);
  return response.data;
}

export async function overrideRagConfig(
  projectId: string,
  customConfig: Record<string, unknown>
): Promise<{ message: string; settings: Record<string, unknown> }> {
  const response = await api.patch(`/projects/${projectId}/rag/override`, {
    custom_config: customConfig,
  });
  return response.data;
}

export async function resetRagConfig(projectId: string): Promise<{ message: string; settings: Record<string, unknown> }> {
  const response = await api.post(`/projects/${projectId}/rag/reset`);
  return response.data;
}

export async function validateRagConfig(projectId: string): Promise<{ valid: boolean; errors: string[] }> {
  const response = await api.get(`/projects/${projectId}/rag/validate`);
  return response.data;
}

// ===========================================
// Project Asset Usage APIs
// ===========================================

function transformAssetUsage(data: Record<string, unknown>): AssetUsage {
  return {
    id: data.id as string,
    asset: transformAssetSummary(data.asset as Record<string, unknown>),
    contextType: data.context_type as string | undefined,
    contextId: data.context_id as string | undefined,
    workflowId: data.workflow_id as string | undefined,
    workflowNodeId: data.workflow_node_id as string | undefined,
    inputParams: data.input_params as Record<string, unknown> | undefined,
    outputSummary: data.output_summary as Record<string, unknown> | undefined,
    durationMs: data.duration_ms as number | undefined,
    tokensUsed: data.tokens_used as number | undefined,
    success: data.success as boolean,
    errorMessage: data.error_message as string | undefined,
    createdAt: data.created_at as string,
    user: data.user as { id: string; name: string } | undefined,
  };
}

export async function getProjectAssetUsages(
  projectId: string,
  params: {
    type?: string;
    contextType?: string;
    group?: boolean;
    page?: number;
    perPage?: number;
  } = {}
): Promise<{ usages: AssetUsage[]; pagination?: Record<string, unknown> }> {
  const queryParams = new URLSearchParams();

  if (params.type) queryParams.append('type', params.type);
  if (params.contextType) queryParams.append('context_type', params.contextType);
  if (params.group) queryParams.append('group', 'true');
  if (params.page) queryParams.append('page', String(params.page));
  if (params.perPage) queryParams.append('per_page', String(params.perPage));

  const response = await api.get(`/projects/${projectId}/project_assets?${queryParams.toString()}`);

  return {
    usages: response.data.usages?.map(transformAssetUsage) || [],
    pagination: response.data.pagination,
  };
}

export async function getAvailableProjectAssets(
  projectId: string,
  params: {
    type?: string;
    category?: string;
  } = {}
): Promise<{ assets: AssetSummary[]; counts: Record<string, number> }> {
  const queryParams = new URLSearchParams();

  if (params.type) queryParams.append('type', params.type);
  if (params.category) queryParams.append('category', params.category);

  const response = await api.get(`/projects/${projectId}/project_assets/available?${queryParams.toString()}`);

  return {
    assets: response.data.assets.map(transformAssetSummary),
    counts: response.data.counts,
  };
}

export async function recordAssetUsage(
  projectId: string,
  params: {
    assetId: string;
    contextType?: string;
    contextId?: string;
    workflowId?: string;
    workflowNodeId?: string;
    inputParams?: Record<string, unknown>;
  }
): Promise<AssetUsage> {
  const response = await api.post(`/projects/${projectId}/project_assets`, {
    asset_id: params.assetId,
    context_type: params.contextType,
    context_id: params.contextId,
    workflow_id: params.workflowId,
    workflow_node_id: params.workflowNodeId,
    input_params: params.inputParams,
  });

  return transformAssetUsage(response.data.usage);
}

export async function deleteAssetUsage(projectId: string, usageId: string): Promise<void> {
  await api.delete(`/projects/${projectId}/project_assets/${usageId}`);
}

// ===========================================
// Export all
// ===========================================

export default {
  // Global Assets
  getAssets,
  getAssetsGrouped,
  getAssetTypes,
  getAsset,
  getAssetBySlug,
  createAsset,
  updateAsset,
  deleteAsset,
  getAssetStats,

  // RAG Modules
  getRagModules,
  getRagPipelines,

  // Project RAG Config
  getProjectRagConfig,
  applyRagPreset,
  composeRagPipeline,
  overrideRagConfig,
  resetRagConfig,
  validateRagConfig,

  // Project Asset Usage
  getProjectAssetUsages,
  getAvailableProjectAssets,
  recordAssetUsage,
  deleteAssetUsage,
};
